// Platform defaults for dealership pay model + bonus reward bundles.
//
// The advisor dashboard, tour, and settings form all read through these so
// a brand-new dealership (manager hasn't touched settings yet) still ships
// a complete, meaningful experience. The manager can save-as-is or tune
// individual values.
//
// Discipline:
//   - **Pay-model rewards (per estimate + per paid) default ON.** These
//     are the platform's core promise: every estimate pays, every paid
//     estimate pays more. Transparent, no chase.
//   - **Bonus rewards default OFF.** Activation milestones, weekly/monthly
//     goal hits, streak bonuses, leaderboards — these are opt-in layers
//     a manager turns on if they want extra motivation. Default OFF
//     keeps the dashboard a wallet, not a competition surface.
//   - Monetary amounts stay low out of the box ($5 estimate / $50 paid)
//     so any dealership can opt in without sticker shock; the Service
//     Lane calculator suggests tuned rates anchored to projected revenue.
//   - **Competition modules default OFF** for the same reason as bonuses —
//     the manager only pays what they explicitly enable.

import type {
  CompetitionModuleConfig,
  CompetitionModuleKey,
  DealershipGoalsRewards,
  DealershipLeaderboardBundle,
  DealershipRewardBundle,
  DealershipRewardTrigger,
  PayoutCadence,
  ServiceLaneBaseline,
} from "@/lib/api/types"

interface BundleDefault {
  badgeLabel: string
  badgeOnByDefault: boolean
  monetary: number
  // When true, both monetary AND the trigger overall are on out of the box.
  // Used to mark the core pay-model rewards (per-estimate, per-paid) as
  // always-on by default. Bonus triggers leave this false so they only
  // fire after the manager explicitly turns them on.
  enabledOnByDefault?: boolean
}

// Trigger defaults — badge label always present (so toggling it on
// pre-fills with something sensible), but `badgeOnByDefault` decides
// whether the badge fires at all out of the box. Monetary amount is the
// default payout when monetary is on.
export const TRIGGER_DEFAULTS: Record<DealershipRewardTrigger, BundleDefault> = {
  perEstimateSubmitted: {
    badgeLabel: "Estimate submitted",
    badgeOnByDefault: false,
    monetary: 5,
    enabledOnByDefault: true,
  },
  perEstimateCompleted: {
    badgeLabel: "Estimate paid",
    badgeOnByDefault: false,
    monetary: 50,
    enabledOnByDefault: true,
  },
  activationFirstEstimate: {
    badgeLabel: "On the board",
    badgeOnByDefault: false,
    monetary: 5,
  },
  activation5DayStreak: {
    badgeLabel: "5-day milestone",
    badgeOnByDefault: false,
    monetary: 25,
  },
  activation30DayStreak: {
    badgeLabel: "30-day milestone",
    badgeOnByDefault: false,
    monetary: 100,
  },
  weeklyGoalHit: {
    badgeLabel: "Weekly goal",
    badgeOnByDefault: false,
    monetary: 10,
  },
  monthlyGoalHit: {
    badgeLabel: "Monthly goal",
    badgeOnByDefault: false,
    monetary: 50,
  },
  weeklyStreak4Bonus: {
    badgeLabel: "4-week run",
    badgeOnByDefault: false,
    monetary: 50,
  },
}

// Triggers that ship turned-on by default. The Pay Model UI shows these
// as the always-visible core; the Bonuses section toggles the rest.
export const CORE_PAY_TRIGGERS: ReadonlyArray<DealershipRewardTrigger> = [
  "perEstimateSubmitted",
  "perEstimateCompleted",
]

export function isCorePayTrigger(t: DealershipRewardTrigger): boolean {
  return CORE_PAY_TRIGGERS.includes(t)
}

export const DEFAULT_PAYOUT_CADENCE: PayoutCadence = "biweekly"

// Payout periods require manager sign-off out of the box — auto-approval
// is an explicit opt-in once the manager trusts the computed amounts.
export const DEFAULT_PAYOUT_MODE: NonNullable<DealershipGoalsRewards["payoutMode"]> = "manual"

export type CompetitionCadence = "daily" | "weekly" | "streak" | "team"

export interface CompetitionModuleDefault {
  title: string
  description: string
  cadence: CompetitionCadence
  defaultAmount: number
  defaultThreshold?: number
  defaultStreakDays?: 5 | 10 | 30
  defaultQualifier?: "top_1" | "top_3" | "above_target"
  defaultWindow?: "weekly" | "monthly"
}

// Competition module registry — titles, copy, and default prize amounts.
// A "valid scan" is an estimate record: each estimate an advisor creates
// counts as one scan. The settings UI iterates this; the advisor dashboard
// reads the same registry for module titles.
export const COMPETITION_DEFAULTS: Record<CompetitionModuleKey, CompetitionModuleDefault> = {
  dailyScanChampion: {
    title: "Daily scan champion",
    description: "Pays the advisor with the most valid scans each day.",
    cadence: "daily",
    defaultAmount: 25,
  },
  firstScanOfDay: {
    title: "First scan of the day",
    description: "Pays whoever logs the day's first scan — rewards showing up early.",
    cadence: "daily",
    defaultAmount: 10,
  },
  hotStreak: {
    title: "Hot streak bonus",
    description: "Pays any advisor who hits the scan threshold in a single day.",
    cadence: "daily",
    defaultAmount: 20,
    defaultThreshold: 5,
  },
  fastStart: {
    title: "Fast start bonus",
    description: "Pays the first advisor to reach the target scan count each day.",
    cadence: "daily",
    defaultAmount: 15,
    defaultThreshold: 3,
  },
  weeklyVolumeLeaderboard: {
    title: "Weekly volume leaderboard",
    description: "Pays the week's top scanners by total volume.",
    cadence: "weekly",
    defaultAmount: 100,
    defaultQualifier: "top_1",
    // Fallback scan target for the "above target" qualifier so it pays as
    // advertised even before the manager sets an explicit weekly target.
    defaultThreshold: 20,
  },
  comebackChallenge: {
    title: "Comeback challenge",
    description: "Pays the advisor with the biggest week-over-week scan improvement.",
    cadence: "weekly",
    defaultAmount: 50,
  },
  beatPersonalBest: {
    title: "Beat your personal best",
    description: "Pays any advisor who tops their own best weekly scan count.",
    cadence: "weekly",
    defaultAmount: 30,
  },
  streakBonus: {
    title: "Streak bonus",
    description: "Pays after consecutive workdays with at least one scan.",
    cadence: "streak",
    defaultAmount: 50,
    defaultStreakDays: 5,
  },
  teamGoal: {
    title: "Team goal bonus",
    description: "Pays every advisor when the team hits a collective scan target.",
    cadence: "team",
    defaultAmount: 50,
    defaultThreshold: 100,
    defaultWindow: "weekly",
  },
}

/**
 * Full competition map with every module present, disabled, and its
 * default prize/extras filled in — the seed the settings form hydrates
 * from so toggling a module on never starts from blank fields.
 */
export function defaultCompetitions(): Record<CompetitionModuleKey, CompetitionModuleConfig> {
  const out = {} as Record<CompetitionModuleKey, CompetitionModuleConfig>
  for (const k of Object.keys(COMPETITION_DEFAULTS) as CompetitionModuleKey[]) {
    const d = COMPETITION_DEFAULTS[k]
    const cfg: CompetitionModuleConfig = { enabled: false, amount: d.defaultAmount }
    if (d.defaultThreshold != null) cfg.threshold = d.defaultThreshold
    if (d.defaultStreakDays != null) cfg.streakDays = d.defaultStreakDays
    if (d.defaultQualifier != null) cfg.qualifier = d.defaultQualifier
    if (d.defaultWindow != null) cfg.window = d.defaultWindow
    out[k] = cfg
  }
  return out
}

export const LEADERBOARD_DEFAULT_BADGE_LABEL = "Top of the Month"
export const LEADERBOARD_DEFAULT_MONETARY = 100

function defaultBundle(trigger: DealershipRewardTrigger): DealershipRewardBundle {
  const d = TRIGGER_DEFAULTS[trigger]
  // Core pay-model rewards (per-estimate / per-paid) ship enabled so the
  // dashboard pays from day one. Bonus triggers ship with monetary turned
  // OFF — the manager has to flip them on to enroll the dealership in
  // that bonus layer. Keeps the wallet front-and-center by default.
  const enabled = d.enabledOnByDefault === true
  return {
    badge: { enabled: d.badgeOnByDefault, label: d.badgeLabel },
    monetary: { enabled, amount: d.monetary },
  }
}

// Bonus leaderboard ships OFF by default — competition is a "cherry on
// top," not the platform's first impression. Manager turns it on in the
// Bonuses section of Pay & Rewards if they want it.
function defaultLeaderboard(): DealershipLeaderboardBundle {
  return {
    scope: "top_3",
    badge: { enabled: false, label: LEADERBOARD_DEFAULT_BADGE_LABEL },
    monetary: { enabled: false, amount: LEADERBOARD_DEFAULT_MONETARY },
  }
}

const DAYS_PER_WEEK = 5
const WEEKS_PER_MONTH = 4.33

// Round to the nearest 5 — feels chosen, not calculated.
function roundTo5(n: number): number {
  return Math.max(5, Math.round(n / 5) * 5)
}

/**
 * Derive a sensible weekly target. With baseline data: 80% of viable lane
 * estimates. Without baseline: 5 per advisor.
 */
export function defaultWeeklyTarget(
  baseline: ServiceLaneBaseline | undefined,
  activeAdvisorCount: number,
): number {
  const advisors = Math.max(1, activeAdvisorCount)
  if (baseline?.dailyLaneVolume && baseline.dailyLaneVolume > 0) {
    const pct = baseline.visibleDamagePct ?? 40
    const viableWeek = baseline.dailyLaneVolume * (pct / 100) * DAYS_PER_WEEK
    return roundTo5(viableWeek * 0.8)
  }
  return roundTo5(5 * advisors)
}

export function defaultMonthlyTarget(
  baseline: ServiceLaneBaseline | undefined,
  activeAdvisorCount: number,
): number {
  // Always round UP to the next 5. Rounding-nearest would tie a 5/wk team
  // to a 20/mo monthly target (basically 4 weeks of weekly), which makes
  // the monthly card feel pointless. Rounding up gives the month a
  // genuinely more ambitious target than 4 × weekly.
  const raw = defaultWeeklyTarget(baseline, activeAdvisorCount) * WEEKS_PER_MONTH
  return Math.max(5, Math.ceil(raw / 5) * 5)
}

/**
 * Full default `goalsRewards` payload — used as a fallback when team
 * metadata is empty and as the seed for the settings form on first load.
 */
export function defaultGoalsRewards(
  baseline: ServiceLaneBaseline | undefined,
  activeAdvisorCount: number,
): DealershipGoalsRewards {
  const triggers: DealershipRewardTrigger[] = [
    "perEstimateSubmitted",
    "perEstimateCompleted",
    "activationFirstEstimate",
    "activation5DayStreak",
    "activation30DayStreak",
    "weeklyGoalHit",
    "monthlyGoalHit",
    "weeklyStreak4Bonus",
  ]
  const rewards: Partial<Record<DealershipRewardTrigger, DealershipRewardBundle>> = {}
  for (const t of triggers) rewards[t] = defaultBundle(t)
  return {
    weeklyEstimateTarget: defaultWeeklyTarget(baseline, activeAdvisorCount),
    monthlyEstimateTarget: defaultMonthlyTarget(baseline, activeAdvisorCount),
    rewards,
    leaderboardReward: defaultLeaderboard(),
    payoutCadence: DEFAULT_PAYOUT_CADENCE,
    competitions: defaultCompetitions(),
    payoutMode: DEFAULT_PAYOUT_MODE,
  }
}

/**
 * Merge stored manager-customized values over the platform defaults so
 * partial customizations don't blank out the rest.
 */
export function withGoalsRewardsDefaults(
  stored: DealershipGoalsRewards | undefined,
  baseline: ServiceLaneBaseline | undefined,
  activeAdvisorCount: number,
): DealershipGoalsRewards {
  const fallback = defaultGoalsRewards(baseline, activeAdvisorCount)
  if (!stored) return fallback
  const rewards = { ...fallback.rewards }
  if (stored.rewards) {
    for (const k of Object.keys(stored.rewards) as DealershipRewardTrigger[]) {
      const r = stored.rewards[k]
      if (r) rewards[k] = r
    }
  }
  // Per-key spread so a stored module that only persisted { enabled, amount }
  // still surfaces its default extras (threshold/qualifier/…) in the form.
  const competitions = defaultCompetitions()
  if (stored.competitions) {
    for (const k of Object.keys(stored.competitions) as CompetitionModuleKey[]) {
      const c = stored.competitions[k]
      if (c) competitions[k] = { ...competitions[k], ...c }
    }
  }
  return {
    weeklyEstimateTarget:
      stored.weeklyEstimateTarget != null && stored.weeklyEstimateTarget > 0
        ? stored.weeklyEstimateTarget
        : fallback.weeklyEstimateTarget,
    monthlyEstimateTarget:
      stored.monthlyEstimateTarget != null && stored.monthlyEstimateTarget > 0
        ? stored.monthlyEstimateTarget
        : fallback.monthlyEstimateTarget,
    rewards,
    leaderboardReward: stored.leaderboardReward ?? fallback.leaderboardReward,
    payoutCadence: stored.payoutCadence ?? fallback.payoutCadence,
    competitions,
    payoutMode: stored.payoutMode ?? DEFAULT_PAYOUT_MODE,
    // Carry per-advisor overrides through untouched — dropping them here is
    // what made saved overrides vanish on the settings form's next hydrate.
    perAdvisorGoals: stored.perAdvisorGoals,
  }
}

export function isManagerCustomized(stored: DealershipGoalsRewards | undefined): boolean {
  return (
    !!stored &&
    (stored.weeklyEstimateTarget != null ||
      stored.monthlyEstimateTarget != null ||
      !!stored.rewards ||
      !!stored.leaderboardReward ||
      !!stored.payoutCadence ||
      !!stored.competitions ||
      !!stored.payoutMode ||
      !!stored.perAdvisorGoals)
  )
}

// Resolve the manager-set advisor count first, falling back to the live
// teamUsers-derived count. Both are kept in scope at every call site so
// the calculator and dashboard math always have a sensible number to
// work with, even before the manager fills in the override.
export function resolveActiveAdvisors(
  baseline: ServiceLaneBaseline | undefined,
  inferredCount: number,
): number {
  if (baseline?.activeAdvisors != null && baseline.activeAdvisors > 0) {
    return baseline.activeAdvisors
  }
  return Math.max(1, inferredCount)
}
